<template>
  <div class="admin-conversation-chat-history" ref="adminConversationChatHistoryRef">
    <div class="card">
      <div class="chat-header border d-flex justify-content-between align-items-center px-2">
        <h3 class="mb-0">
          {{ prettyPhoneNumber(conversationDetail.recipients[0].number) }}
        </h3>
        <div class="d-flex align-items-center gap-2">
          <div
            v-if="conversationDetail.artifact_id"
            title="Export Conversation Artifact"
            class="d-flex align-items-center gap-1 fs-2 px-2 py-0 rounded text-light bg-info fw-bold"
            style="cursor: pointer;"
            @click="exportModal(conversationDetail.artifact_id)"
          >
            Export <b-icon-download class="overflow-visible fs-2" />
          </div>
          <div
            v-if="conversationDetail.artifact_id"
            title="Share externally through a magic link"
            class="d-flex align-items-center gap-1 fs-2 px-2 py-0 rounded text-light bg-info fw-bold"
            style="cursor: pointer;"
            @click="shareModal(conversationDetail.artifact_id)"
          >
            Share <b-icon-share class="overflow-visible fs-2" />
          </div>
          <span :class="`convo-${conversationDetail.status} badge font-weight-medium`">
            {{ conversationDetail.status }}
          </span>
        </div>
      </div>
      <div
        class="chat-box card-body border border-top-0 px-0 d-flex flex-column gap-4"
        :class="{
          'bg-light': isDarkMode,
        }"
        :style="{
          height: `${chatBoxHeight}px`
        }"
      >
        <p
          v-for="message in conversationMessages"
          :key="message.message_id"
          class="mb-0"
          :class="{
            'mr-auto': message.direction != 'outbound',
            'ml-auto': message.direction == 'outbound' && message.source != 'note',
            'w-50': !(message.direction == 'outbound' && message.source == 'note')
          }"
        >
          <ul class="chat-list chat d-block">
            <li v-if="message.direction == 'outbound'" class="d-block">
              <div
                v-if="message.source === 'note'"
                class="px-2 py-2"
              >
                <div class="box d-inline-block w-100 text-center font-italic fs-2">
                  {{ message.message_body }}
                  <p class="fs-1 mb-0">{{ formatTimestamp(message.created_at, "HH:mm:ss") }}</p>
                </div>
              </div>

              <div v-else class="chat-content d-flex w-100 px-2 py-2 gap-1">
                <div class="d-flex flex-column gap-1">
                  <div class="d-flex align-items-baseline gap-2">
                    <h5 class="fs-3 text-muted font-weight-bold mx-0 my-0 text-nowrap">
                      {{ message.operator_id ? message.operator : "System Generated Message" }}
                    </h5>
                    <div
                      v-if="message.source === 'sms' || message.source === 'voice'"
                      class="fs-2 text-nowrap"
                    >
                      {{ formatTimestamp(message.created_at, "LT") }}
                    </div>
                    <div
                      v-if="message.source === 'sms' || message.source === 'voice'"
                      class="fs-2"
                      :class="`${message.error_code ? 'text-danger' : ''}`"
                    >
                      <span :title="`${message.error_message ? message.error_message : ''}`">
                        {{ message.status }}
                      </span>
                    </div>
                  </div>
                  <div 
                    class="box d-inline-block text-dark font-weight-medium fs-3 w-100 p-2 rounded"
                    :class="{
                      'bg-light-inverse': !isDarkMode,
                      'bg-dark': isDarkMode,
                    }"
                  >
                    <div
                      v-linkified
                      class="fw-normal"
                      style="
                        white-space: pre-line;
                        word-wrap: break-word;
                      "
                    >
                      {{ message.message_body }}
                    </div>
                    <div
                      v-if="
                        message.body_transl &&
                        message.language &&
                        message.language.substring(0, 2) != 'en'
                      "
                      class="mt-2"
                    >
                      <a
                        data-toggle="collapse"
                        :href="`#id-${message.message_id}_transl`"
                        role="button"
                        aria-expanded="true"
                        :aria-controls="`id-${message.message_id}_transl`"
                        class="font-weight-light chat-message-translated-lang text-dark"
                      >
                        <font-awesome-icon icon="fas fa-caret-right" />
                        {{
                          ['sms', 'chat', 'voice'].includes(message.source)
                            ? `Sent in ${dialectLabel(message.language)}`
                            : `Translated to ${dialectLabel(message.language)}`
                        }}
                      </a>
                      <div
                        class="collapse multi-collapse show"
                        :id="`id-${message.message_id}_transl`"
                      >
                        <div
                          v-linkified
                          class="fw-normal pl-3 fs-2"
                          style="
                            white-space: pre-line;
                            word-wrap: break-word;
                          "
                        >
                          {{ message.body_transl }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="d-flex align-items-cetner justify-content-end text-muted fs-2 font-italic gap-3">
                    <span v-if="message.source === 'voice'">Sent as speech</span>
                    <span v-if="message.source === 'sms'">Sent as SMS</span>
                  </div>
                </div>
              </div>
            </li>

            <li v-else>
              <div class="chat-content d-flex w-100 px-2 py-2 gap-1">
                <!-- <div class="rounded inbound-avatar"></div> -->
                <div class="d-flex flex-column gap-1">
                  <div class="d-flex align-items-baseline gap-2">
                    <h5
                      v-if="message.recipient_number || message.recipient_label"
                      class="fs-3 text-muted font-weight-bold mb-0"
                    >
                      {{
                        message.recipient_label
                          ? prettyPhoneNumber(message.recipient_label)
                          : prettyPhoneNumber(message.recipient_number)
                      }}
                    </h5>
                    <div class="fs-2">
                      {{ formatTimestamp(message.created_at, "LT") }}
                    </div>
                  </div>
                  <div
                    class="box d-inline-block text-dark font-weight-medium fs-3 w-100 p-2 rounded"
                    :class="{
                      'bg-light-info': isDarkMode,
                      'bg-light': !isDarkMode,
                    }"
                    :id="`inbound-message-${message.message_id}`"
                  >
                    <div
                      v-linkified
                      class="fw-normal"
                      style="
                        white-space: pre-line;
                        word-wrap: break-word;
                      "
                    >
                      {{ message.message_body }}
                    </div>
                    <div
                      v-if="
                        message.body_transl &&
                        message.language &&
                        message.language.substring(0, 2) != 'en'
                      "
                      class="mt-2"
                    >
                      <a
                        data-toggle="collapse"
                        :href="`#id-${message.message_id}_transl`"
                        role="button"
                        aria-expanded="true"
                        :aria-controls="`id-${message.message_id}_transl`"
                        class="font-weight-light chat-message-translated-lang text-dark"
                      >
                        <font-awesome-icon icon="fas fa-caret-right" />
                        Translated from {{ dialectLabel(message.language) }}
                      </a>
                      <div class="collapse multi-collapse show" :id="`id-${message.message_id}_transl`">
                        <div
                          v-linkified
                          class="fw-normal pl-3 fs-2"
                          style="
                            white-space: pre-line;
                            word-wrap: break-word;
                          "
                        >
                          {{ message.body }}
                        </div>
                      </div>
                    </div>
                    <div
                      v-if="message.num_media > 0"
                      class="chat-content-attachments d-flex flex-wrap gap-2 mt-2"
                    >
                      <div
                        v-for="attachment in message.messageAttachments"
                        :key="attachment.attachment_id"
                        class="chat-content-attachment"
                      >
                        <ImageItem
                          v-if="attachment.isImage"
                          :attachmentId="attachment.attachment_id"
                          :attachment="attachment"
                          :processed="attachment.status === 'processed'"
                          :image-width="'200px'"
                        />
                        <MediaItem
                          v-if="attachment.isMedia"
                          :attachmentId="attachment.attachment_id"
                          :attachment="attachment"
                          :processed="attachment.status === 'processed'"
                          :media-width="'200px'"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </p>
      </div>
      <v-resizable
        :class="`chat-tabs-resizer ${resizingDrawer ? 'resizing' : ''}`"
        :width="drawerWidth"
        :height="drawerHeight"
        :fitParent="true"
        :top="drawerTop"
        :left="drawerLeft"
        :minWidth="drawerWidth"
        :maxWidth="drawerWidth"
        :minHeight="40"
        :maxHeight="drawerMaxHeight"
        :active="isDetailTabOpened ? ['t'] : []"
        @resize:start="vResizeHandler"
        @resize:move="vResizeHandler"
        @resize:end="vResizeHandler"
      >
        <div
          class="chat-tabs bg-white-custom"
        >
          <div
            class="chat-tabs-header border border-top-transparent"
            :class="{
              'border-bottom': isDetailTabOpened,
              'chat-tabs-header-rounded': !isDetailTabOpened
            }"
          >
            <div class="chat-tabs-header--trigger bg-light border border-top-transparent" @click="toggleConversationDetailTabs">
              <b-icon-chevron-compact-up v-if="!isDetailTabOpened" />
              <b-icon-chevron-compact-down v-else />
            </div>
            <div class="chat-tabs-header--tabs d-flex align-items-center">
              <div
                v-for="(drawerTab, drawerTabIndex) in getDrawerTabs"
                :key="`drawer-tab-${drawerTabIndex}`"
                class="chat-tabs-header--tab"
                :class="{
                  'tab-selected': detailTabOpened === drawerTabIndex,
                  'border-info': detailTabOpened === drawerTabIndex,
                  'text-info': detailTabOpened === drawerTabIndex,
                }"
                @click="openTab(drawerTabIndex)"
              >
                {{ drawerTab.type === 'attachments' ? mediaTabTitle : drawerTab.title }}
              </div>
            </div>
          </div>
          <div v-show="isDetailTabOpened" class="chat-tabs-content border border-top-transparent">
            <b-tabs :value="detailTabOpened">
              <b-tab v-if="isSDKEnabled && agencySettings.uxVoiceEnabled">
                <AdminTranscribeTab :conversationId="conversationId" />
              </b-tab>
              <b-tab v-if="isSDKEnabled && agencySettings.uxVideoEnabled">
                <AdminVideoTab />
              </b-tab>
              <b-tab>
                <AdminMediaTab
                  :conversationMessages="conversationMessages"
                  :conversationAttachments="conversationAttachments"
                  :opened="isDetailTabOpened && detailTabOpened === mediaTabIndex"
                />
              </b-tab>
            </b-tabs>
          </div>
        </div>
      </v-resizable>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import ImageItem from "@/components/ImageItem";
import MediaItem from "@/components/MediaItem";
import formatter from "@/mixins/formatter";
import AdminMediaTab from "@/components/conversations/MediaTab";
import AdminTranscribeTab from "@/components/conversations/TranscribeTab";
import AdminVideoTab from "@/components/conversations/VideoTab";
import { mapGetters } from "vuex";
import VResizable from "vue-resizable";
import { EventBus } from "@/events/eventBus";
import ShareExportModal from "@/components/admin/ShareExportModal";

export default {
  name: "AdminConversationChatHistory",
  mixins: [formatter],
  props: {
    conversationDetail: Object,
    conversationMessages: {
      type: Array,
      default: () => [],
    },
    conversationAttachments: {
      type: Array,
      default: () => [],
    }
  },
  components: {
    ImageItem,
    MediaItem,
    AdminMediaTab,
    AdminTranscribeTab,
    AdminVideoTab,
    VResizable,
  },
  data() {
    return {
      isDetailTabOpened: false,
      detailTabOpened: 0,
      drawerTabs: [
        {
          type: 'voice',
          title: 'Voice',
          enabled: false,
        },
        {
          type: 'video',
          title: 'Video',
          enabled: false,
        },
        {
          type: 'attachments',
          enabled: true,
        },
      ],
      chatBoxHeight: 0,
      drawerTop: 0,
      drawerLeft: 0,
      drawerWidth: 0,
      drawerHeight: 40,
      drawerMaxHeight: 40,
      resizingDrawer: false,
    };
  },
  computed: {
    ...mapGetters("user", ["isDarkMode", "viewport", "userSidebarOpened"]),
    ...mapGetters("agency", ["agencySettings", "isSDKEnabled", "textDialects"]),
    dialects() {
      return this.textDialects || [];
    },
    mediaTabTitle() {
      const mediaCount = this.conversationAttachments.filter(attachment => attachment.content_type !== 'audio/pcm').length;
      return `Media (${mediaCount})`;
    },
    mediaTabIndex() {
      const tabIndex = this.getDrawerTabs.findIndex(tab => tab.type === 'attachments');
      return tabIndex >= 0 ? tabIndex : 0;
    },
    getDrawerTabs() {
      return this.drawerTabs
        .map(tab => {
          let enabled = false;
          if (tab.type === 'voice') {
            enabled = this.isSDKEnabled && this.agencySettings.uxVoiceEnabled;
          } else if (tab.type === 'video') {
            enabled = this.isSDKEnabled && this.agencySettings.uxVideoEnabled;
          } else {
            enabled = true;
          }
          return {
            ...tab,
            enabled,
          }
        })
        .filter(tab => tab.enabled);
    },
    conversationId() {
      return this.$route.params.conversationId;
    },
  },
  created() {
    this.$nextTick(() => {
      this.initOpenedChatTabSize();
      this.isDetailTabOpened = true;
      this.resizingDrawer = false;
    });
  },
  methods: {
    toggleConversationDetailTabs() {
      this.resizingDrawer = false;
      if (this.isDetailTabOpened) {
        this.initClosedChatTabSize();
      } else {
        this.initOpenedChatTabSize();
      }
      this.isDetailTabOpened = !this.isDetailTabOpened;
    },
    initClosedChatTabSize() {
      if (
        this.$refs.adminConversationChatHistoryRef &&
        this.$refs.adminConversationChatHistoryRef.clientHeight &&
        this.$refs.adminConversationChatHistoryRef.clientWidth
      ) {
        this.drawerWidth = this.$refs.adminConversationChatHistoryRef.clientWidth;
        this.drawerHeight = 40;
        this.chatBoxHeight = this.$refs.adminConversationChatHistoryRef.clientHeight - 41 - 40;
        this.drawerLeft = 0;
        this.drawerTop = 41 + this.chatBoxHeight;
        this.drawerMaxHeight = this.$refs.adminConversationChatHistoryRef.clientHeight - 41 - 100;
      }
    },
    initOpenedChatTabSize() {
      if (
        this.$refs.adminConversationChatHistoryRef &&
        this.$refs.adminConversationChatHistoryRef.clientHeight &&
        this.$refs.adminConversationChatHistoryRef.clientWidth
      ) {
        this.drawerWidth = this.$refs.adminConversationChatHistoryRef.clientWidth;
        this.drawerHeight = (this.$refs.adminConversationChatHistoryRef.clientHeight - 41) / 2;
        this.chatBoxHeight = (this.$refs.adminConversationChatHistoryRef.clientHeight - 41) / 2;
        this.drawerLeft = 0;
        this.drawerTop = 41 + this.chatBoxHeight;
        this.drawerMaxHeight = this.$refs.adminConversationChatHistoryRef.clientHeight - 41 - 100;
      }
    },
    openTab(tab) {
      this.detailTabOpened = tab;
      if (!this.isDetailTabOpened) {
        this.toggleConversationDetailTabs();
      }
    },
    vResizeHandler($event) {
      if (this.isDetailTabOpened) {
        const { eventName, height, top } = $event;
        if (eventName !== "resize:end") {
          this.resizingDrawer = true;
        } else {
          this.resizingDrawer = false;
        }
        if (height > 100) {
          if (height <= this.drawerMaxHeight) {
            this.drawerHeight = height;
            this.drawerTop = top;
            this.chatBoxHeight = this.$refs.adminConversationChatHistoryRef.clientHeight - 41 - height;
          }
        } else {
          this.isDetailTabOpened = false;
          this.initClosedChatTabSize();
        }
      }
    },
    shareModal(id) {
      const $ = window.$;
      $("#id-modal-container").modal({
        backdrop: "static"
      });
      EventBus.$emit("open", {
        component: ShareExportModal,
        title: "Share Conversation Artifact",
        actionButton: "Share",
        closeOnClick: false,
        props: {
          artifactId: id,
          action: "share"
        }
      });
    },
    exportModal(id) {
      const $ = window.$;
      $("#id-modal-container").modal({
        backdrop: "static"
      });
      EventBus.$emit("open", {
        component: ShareExportModal,
        title: "Export Conversation Artifact",
        actionButton: "Download",
        closeOnClick: false,
        props: {
          artifactId: id,
          action: "export"
        }
      });
    },
  },
  watch: {
    viewport: {
      handler(viewport) {
        if (this.isDetailTabOpened) {
          this.initOpenedChatTabSize()
        } else {
          this.initClosedChatTabSize();
        }
      },
      deep: true
    },
    userSidebarOpened(value) {
      if (this.isDetailTabOpened) {
        this.initOpenedChatTabSize()
      } else {
        this.initClosedChatTabSize();
      }
    }
  }
};
</script>

<style lang="scss">
.admin-conversation-chat-history {
  height: 100%;
  overflow-y: auto;

  >div {
    height: 100%;
  }

  .card {
    height: 100%;
    margin: 0;
    border-radius: 1rem;
    border: none !important;

    .chat-header {
      width: 100%;
      height: 41px;
      min-height: 41px;
      max-height: 41px;
      border-radius: 1rem 1rem 0 0;
    }

    .chat-box {
      transition: height ease-in-out 0.15s;
      overflow-y: auto;

      .chat-content {
        width: 100%;
        overflow-x: auto;
      }

      >p {
        &:first-child {
          >ul {
            >li {
              >div {
                border: none !important;
              }
            }
          }
        }
      }

      div .chat-message-translated {
        color: #8f8f8f;
        margin-top: 10px;
      }

      .chat-message-translated-lang {
        font-size: small;
        font-style: italic;
        color: #8f8f8f;
      }
    }

    .chat-tabs-resizer {
      bottom: 0 !important;
      top: 0 !important;
    }

    .chat-tabs {
      display: grid;
      grid-template-rows: 40px 1fr;
      height: 100%;
      transition: height ease-in-out 0.15s;
      border-radius: 0 0 1rem 1rem;

      &-header {
        position: relative;

        &--trigger {
          position: absolute;
          top: 0;
          right: calc(50% - 16px);
          padding: 0 8px;
          cursor: pointer;
          border-radius: 0 0 4px 4px;
          z-index: 2;
          opacity: 0.7;

          &:hover {
            opacity: 1;
          }

          &:focus {
            opacity: 1;
          }
        }

        &--tabs {
          height: 100%;
          gap: 4px;
          padding: 0 4px;

          .chat-tabs-header--tab {
            width: auto;
            height: 100%;
            cursor: pointer;
            border-bottom: 2px solid transparent;
            padding: 8px;
            font-weight: normal;
            white-space: nowrap;

            &.tab-selected {
              // font-weight: bold;
            }

            @media screen and (max-width: 767px) {
              font-size: 12px;
              padding: 10px 4px;
            }
          }
        }

        &.chat-tabs-header-rounded {
          border-radius: 0 0 1rem 1rem;
          overflow: hidden;
          .chat-tabs-header--tabs {
            width: 100%;
          }
        }
      }

      &-content {
        width: 100%;
        height: 100%;
        overflow: hidden;
        overflow-y: auto;
        border-radius: 0 0 1rem 1rem;

        >.tabs {
          height: 100%;

          >div:first-child {
            display: none;
          }
          .tab-content {
            height: 100%;
            .tab-pane {
              height: 100%;

              div[id^=voice-sdk-container] {
                width: 100%;
                height: 100%;

                >div {
                  width: 100% !important;
                  height: 100% !important;

                  div.ConveyEmbed--ActionsContainer {
                    height: 40px !important;
                    border-top: none !important;
                  }
                  div.ConveyEmbed--TranscribeWrap {
                    position: unset !important;
                    width: 100%;
                    height: calc(100% - 40px) !important;
                    padding: 0 !important;
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  .chat-content-attachments {

    &.image-attachments {
      .chat-content-attachment {
      }
    }

    &.media-attachments {
    }
  }
}
</style>